/** Which surface a screen sits on, so the strip under the status bar and the bar icons match
 * the screen beneath (no white-to-green jump on the native shells). `html[data-surface]`
 * paints the strip (styles.css); this hook sets the attribute and syncs the bars through the
 * `Capabilities` interface, with the theme-color meta as the browser fallback.
 *
 * THE RULE (2026-09-11, owner): the student app wears the THEME, never the screen. Light is
 * white throughout, including the status bar; dark is the board green throughout, including
 * the status bar. `student` is that surface: it reads `--bg`, which styles.css remaps to pure
 * white on the light theme and leaves as the board on the dark one, so the bar and the page
 * under it are the same colour in both. `board` stays what it always was — the surface the
 * whole ROOM looks at (the landing hero, the projector), never a student's phone.
 */
import { useEffect } from "react";

import { capabilities, type BarContrast } from "./capabilities";

export type Surface = "board" | "paper" | "student";

/** The theme is read once from localStorage app.theme in index.html; there is no in-app
 * toggle today, so the attribute and the media query are the whole truth. */
function isDark(): boolean {
  const t = document.documentElement.dataset.theme;
  if (t === "dark") return true;
  if (t === "light") return false;
  return matchMedia("(prefers-color-scheme: dark)").matches;
}

const token = (name: string): string => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function syncSystemBars(): void {
  const surface = (document.documentElement.dataset.surface ?? "paper") as Surface;
  const contrast: BarContrast = surface === "board" || isDark() ? "light-content" : "dark-content";
  // Every surface but the board paints from `--bg`, and the student remap has already put the
  // right value there for this theme: one lookup, one colour, page and bar agreeing by
  // construction rather than by a second table that could drift.
  const colour = token(surface === "board" ? "--board" : "--bg");
  if (capabilities.systemBars) {
    void capabilities.systemBars.setContrast(contrast);
    if (colour) void capabilities.systemBars.setColour(colour);
  } else if (colour) {
    document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
      m.content = colour;
    });
  }
}

/** Declare the screen's surface for as long as it is mounted; paper is the resting state. */
export function useSurface(surface: Surface): void {
  useEffect(() => {
    document.documentElement.dataset.surface = surface;
    syncSystemBars();
    return () => {
      document.documentElement.dataset.surface = "paper";
      syncSystemBars();
    };
  }, [surface]);
}

if (typeof window !== "undefined") {
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", syncSystemBars);
  // The native bar is not styled by CSS, so index.html's pre-paint script cannot set it: do it
  // as soon as this module loads, before any route has mounted, so the shell does not show one
  // frame of the launch colour over a page of the other theme.
  syncSystemBars();
}
